import { createStore, createEvent, createEffect, combine } from 'effector';
import IUser from '@interfaces/IUser';
import request from '@helpers/request';
import api from '@constants/endpoints';
import { PaginationProps } from 'antd/lib/pagination';
import createPaginationStore from './pagination';
import createPreloadersStore from './preloaders';
import createFiltersStore from './filters';
import removeUndefinedFilters from '@helpers/removeUndefinedFilters';
import { get } from 'lodash';
import localeWeight, { LOCALE } from '@stores/localeWeight';
import type { UserBlockReason } from '@constants/userBlockOptions';

export const reset = createEvent('Users store reset');

const defaultFilters = {
  sort: 'id,desc',
};

export const getList = createEffect({
  name: 'getList',
  async handler(params?: {
    pagination?: PaginationProps;
    filters?: Record<string, any>;
    sorter?: any;
  }) {
    const paginationParams: PaginationProps = {
      ...pagination.getState(),
      ...params?.pagination,
    };

    let filtersParams = filters.getState();

    const sorterParams: { sort?: string } = { sort: filtersParams.sort };

    if (params?.sorter) {
      sorterParams.sort = params.sorter.order
        ? `${params.sorter.columnKey},${params.sorter.order.replace('end', '')}`
        : defaultFilters.sort;
    }

    if (params?.sorter && !params.filters) {
      setFilters({ ...filtersParams, ...sorterParams });
    }

    if (params?.filters) {
      setFilters({ ...params.filters, ...sorterParams });
      filtersParams = params.filters;
      paginationParams.current = 1;
    }

    const { current } = paginationParams;

    const requestParams = {
      paged: true,
      size: paginationParams.pageSize ?? localStorage.getItem('usersPageSize'),
      page: (current || 1) - 1,
      ...defaultFilters,
      ...removeUndefinedFilters({
        ...filtersParams,
        locale: localeWeight.getState() === LOCALE.ALL ? undefined : localeWeight.getState(),
      }),
      ...sorterParams,
    };

    const response = await request(api.users.getList, {
      params: requestParams,
    });
    return response;
  },
});

export const blockUser = createEffect({
  name: 'blockUser',
  async handler({
    userId,
    user,
    daysToUnblock,
    daysToClean,
    reason,
  }: {
    userId: IUser['id'];
    preloaderId: string;
    user: IUser;
    daysToUnblock?: number;
    daysToClean?: number;
    reason?: UserBlockReason;
  }) {
    const response = await request(api.users.block, {
      urlParams: { userId },
      params: {
        daysToUnblock,
        daysToClean,
        reason,
      },
    });
    const updatedUser = get(response, ['data', 'body'], null);
    if (updatedUser) {
      return response;
    } else {
      return { data: { body: { ...user, enable: false } } };
    }
  },
});

export const unblockUser = createEffect({
  name: 'unblockUser',
  async handler({ userId, user }: { userId: IUser['id']; user: IUser; preloaderId: string }) {
    const response = await request(api.users.unblock, {
      urlParams: { userId },
    });
    const updatedUser = get(response, ['data', 'body'], null);
    if (updatedUser) {
      return response;
    } else {
      return { data: { body: { ...user, enable: true } } };
    }
  },
});

export const makeAdmin = createEffect({
  name: 'makeAdmin',
  async handler({ userId }: { userId: IUser['id']; preloaderId: string }) {
    const response = await request(api.users.makeadmin, {
      urlParams: { userId },
    });
    return response;
  },
});

export const makeUser = createEffect({
  name: 'makeUser',
  async handler({ userId }: { userId: IUser['id']; preloaderId: string }) {
    const response = await request(api.users.makeuser, {
      urlParams: { userId },
    });
    return response;
  },
});

export const deleteUser = createEffect({
  name: 'deleteUser',
  async handler(userId: IUser['id']) {
    const response = await request(api.users.deleteUser, {
      urlParams: { userId },
    });
    return response;
  },
});

export const updateUser = createEffect({
  name: 'updateUser',
  async handler({
    userId,
    data,
  }: {
    userId: IUser['id'];
    data: Partial<IUser>;
    preloaderId: string;
  }) {
    const response = await request(api.users.updateUser, {
      urlParams: { userId },
      data,
    });
    return response;
  },
});

export const unlinkDiscord = createEffect({
  name: 'unlinkDiscord',
  async handler({ userId, comment }: { userId: IUser['id']; comment: string }) {
    const trimmedComment = comment.trim();
    if (!trimmedComment) throw new Error('A support ticket comment is required');

    return request(api.users.unlinkDiscord, {
      urlParams: { userId },
      data: { comment: trimmedComment },
    });
  },
});

export const createUser = createEffect({
  name: 'createUser',
  async handler(data: Partial<IUser>) {
    const response = await request(api.users.createUser, {
      data,
    });
    return response;
  },
});

export const verifyUser = createEffect({
  name: 'verifyUser',
  async handler({
    userId,
    data,
  }: {
    userId: IUser['id'];
    data: Partial<IUser>;
    preloaderId: string;
  }) {
    const response = await request(api.users.verifyUser, {
      urlParams: { userId },
      data,
    });
    return response;
  },
});

export const recoverPassword = createEffect({
  name: 'recoverPassword',
  async handler(data: Partial<IUser>) {
    const response = await request(api.users.recoverPassword, {
      data: {
        body: data,
        stage: 'EMAIL',
      },
    });
    return response;
  },
});

const updateUserHandler = (
  state: IUser[],
  {
    params,
    result,
  }: {
    params: { userId: IUser['id']; preloaderId: string };
    result: { data: { body: IUser } };
  },
) => {
  const newState = [...state];
  const updatedUserIndex = state.findIndex((user) => user.id === params.userId);
  if (updatedUserIndex === -1) return state;
  const updatedUser = get(result, ['data', 'body'], null);
  if (updatedUser) {
    newState.splice(updatedUserIndex, 1, updatedUser);
    return newState;
  } else {
    return newState;
  }
};

const updateVerifyUserHandler = (
  state: IUser[],
  {
    params,
    result,
  }: {
    params: { userId: IUser['id']; preloaderId: string };
    result: { data: { body: IUser } };
  },
) => {
  const newState = [...state];
  const updatedUserIndex = state.findIndex((user) => user.id === params.userId);
  if (updatedUserIndex === -1) return state;
  const updatedUser = get(result, ['data', 'body'], null);
  if (updatedUser) {
    newState.splice(updatedUserIndex, 1, {
      ...newState[updatedUserIndex],
      isOfficial: updatedUser.isOfficial,
    });
    return newState;
  } else {
    return newState;
  }
};

const updateListHandler = () => {
  getList();
};

const { pagination, setPagination } = createPaginationStore({
  getList,
  listName: 'users',
  resetEvent: reset,
  bodyPath: ['data', 'body'],
});

export { setPagination };

const preloaders = createPreloadersStore([
  getList,
  blockUser,
  unblockUser,
  makeAdmin,
  makeUser,
  createUser,
  updateUser,
  unlinkDiscord,
  deleteUser,
  verifyUser,
  recoverPassword,
]);

const list = createStore<IUser[]>([])
  .on(getList.done, (state, { result }) => get(result, ['data', 'body', 'content']))
  .on(blockUser.done, updateUserHandler)
  .on(unblockUser.done, updateUserHandler)
  .on(makeAdmin.done, updateUserHandler)
  .on(makeUser.done, updateUserHandler)
  .on(deleteUser.done, updateListHandler)
  .on(createUser.done, updateListHandler)
  .on(updateUser.done, updateUserHandler)
  .on(unlinkDiscord.done, updateListHandler)
  .on(verifyUser.done, updateVerifyUserHandler)
  .reset(reset);

const { filters, setFilters } = createFiltersStore({
  listName: 'users',
  deaultValues: defaultFilters,
  resetEvent: reset,
});

export { setFilters };

const users = combine({ preloaders, list, pagination, filters });

export default users;
